/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    Checkbox,
    Pagination,
    Table,
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    Button,
    Dialog,
    DialogContent,
    Alert,
    SearchInput,
    Popover,
    PopoverTrigger,
    PopoverContent,
    DatePicker,
    FilterButton,
    MoreButton,
    Card,
    AlertDialog,
    AlertDialogContent,
} from "@jhool-io/fe-components";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import {
    FlaggedNoteForAuditStatus,
    INote,
    NoteLabels,
    NoteListSortAttribute,
    NoteStatus,
    NoteTypes,
} from "../../../../modules/notes/types/notes.types";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useFetchNotesList } from "../../../../hooks/queries/note";
import ListState from "../../../ListState/ListState";
import styles from "../NoteListWrapper/NoteListWrapper.module.scss";
import { CPT_CODES, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import useToast from "../../../../hooks/useToast";
import ChangeCode from "../../NoteContent/ChangeCode/ChangeCode";
import {
    useChangeCptCode,
    useChangeNoteArchiveStatus,
    useDeleteDraftNote,
    useExportNotes,
    useRecalculateUnitMetadata,
    useSkipSupervisorReview,
} from "../../../../hooks/mutations/note";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import ArchiveNote from "../../NoteContent/ArchiveNote/ArchiveNote";
import ApproveNote from "../../ApproveNote/ApproveNote";
import { useExportPdfs } from "../../../../hooks/queries";
import {
    getCptCodeToDisplay,
    getPdfExportableNoteIds,
    getFlaggedNoteStatusTag,
    getNoteStatusTag,
    getNoteTypeTextWithTag,
    getSelectedAuthorName,
    isNotePdfExportable,
    isPsychNote,
    updateAuthorFilterSearchParams,
} from "../../../../modules/notes/helpers/notes.helpers";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import InsuranceProviderFilter, {
    getInsuranceProviderIdsFilter,
    setInsuranceProviderIdsFilter,
} from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import CheckPrimaryColorIcon from "../../../Icons/CheckPrimaryColor";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import FlagNoteForm from "../../../../modules/notes/components/FlagNoteForm/FlagNoteForm";
import useShowInsuranceOverSelfPay from "../../../../modules/practice-settings/hooks/useShowInsuranceOverSelfPay";
import { useCanCreateNote } from "../../../../modules/notes/hooks/useCanCreateNote";
import { useAppSelector } from "../../../../hooks/useRedux";
import Loader from "../../../../shared-ui/Loader/Loader";
import ExportLoaderAndText from "../../../../shared-ui/ExportLoaderAndText/ExportLoaderAndText";
import { useFetchProviders } from "../../../../modules/providers/hooks/providers.queries";
import ChangeCodeFormPsych from "../../../../modules/notes/components/ChangeCodeFornPsych/ChangeCodeFormPsych";
import { getNotesRecoveryPage } from "../../../../modules/notes/helpers/pagination";

type Dialogs =
    | "loader"
    | "delete-confirmation"
    | "approve"
    | "archive-note"
    | "change-code"
    | "flag_note"
    | "unflag_note"
    | "skip-supervisor-review";

export interface FilterTypes {
    status: string[];
    type: string[];
    cptCode: string;
    insuranceProvider: string;
    fromDate: null | Date;
    toDate: null | Date;
    page: number;
}

interface NoteListProps {
    viewAll?: boolean;
    setNotesCount?: (count: string) => void;
}

export default function NoteList({ viewAll, setNotesCount }: NoteListProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [rowSelection, setRowSelection] = React.useState({});
    const [codeToConfirmId, setCodeToConfirmId] = React.useState<string | null>(
        null
    );
    const [selectedNotesId, setSelectedNotesId] = React.useState<string[]>([]);
    const [jobId, setJobId] = React.useState<string | null>(null);
    const [pdfCount, setPdfCount] = React.useState(0);
    const [showAuthorPopover, setShowAuthorPopover] = React.useState(false);
    const [providerNameSearchString, setProviderNameSearchString] =
        React.useState("");
    const [authorSearchString, setAuthorSearchString] = React.useState("");
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<INote>[]>();

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: searchParams.get("sort_attr") || "",
        direction: searchParams.get("sort_order") || "",
    });

    const loggedInUser = useFetchUserDetails();
    const { practice } = useAppSelector((state) => state.userPractice);

    // Get filters from url
    const clientFilter = searchParams.get("search_string") || "";
    const providerNameFilter = searchParams.get("provider_name") || "";
    const authorFilter = searchParams.get("author") || "";
    const authorNameFilter = searchParams.get("author_name") || "";
    const cptCodeFilter = searchParams.get("cpt_code") || "";
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);
    const statusFilter = searchParams.getAll("status[]");
    const noteTypeFilter = searchParams.getAll("type[]");
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const showArchivedFilter = searchParams.get("show_archived") || "false";
    const fromDateCreatedFilter = searchParams.get("from_date_created");
    const toDateCreatedFilter = searchParams.get("to_date_created");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const debouncedClienFilter = useDebounce(clientFilter);
    const debouncedProviderNameSearchString = useDebounce(
        providerNameSearchString
    );
    const debouncedAuthorSearchString = useDebounce(authorSearchString);

    // date filters values for DatePicker component
    // Converts the string values to a date in the correct timezone

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const fromDateCreatedFilterToDate = convertDateFilterStringToDate(
        fromDateCreatedFilter
    );
    const toDateCreatedFilterToDate =
        convertDateFilterStringToDate(toDateCreatedFilter);

    const handleResetProvidersSearchString = () => {
        setProviderNameSearchString("");
        setAuthorSearchString("");
    };

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const providers = useFetchProviders({
        search_string:
            debouncedProviderNameSearchString || debouncedAuthorSearchString,
        page: 1,
        limit: 20,
    });

    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    const sortableColumns = [
        "date_of_service",
        "client_name",
        "provider_name",
        "created_at",
        "author",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key as NoteListSortAttribute;
        }

        return undefined;
    };

    const tabInView = searchParams.get("tab");

    // Hook for fetching all notes
    const { data, isLoading, isSuccess, error } = useFetchNotesList(
        {
            status: statusFilter,
            cpt_code: cptCodeFilter,
            type: noteTypeFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            from_created_at_date: fromDateCreatedFilter,
            to_created_at_date: toDateCreatedFilter,
            search_string: debouncedClienFilter,
            provider_name: providerNameFilter,
            page: pageFilter,
            insurance_provider_ids: insuranceProviderIdsFilter,
            limit: limitFilter,
            show_archived: showArchivedFilter,
            sort_attribute:
                searchParams.get("sort_attr") || handleConfigAttribute(),
            sort_order: searchParams.get("sort_order") || sortConfig.direction,
            author_id: authorFilter,
            tab_name: viewAll ? "all_notes" : "",
        },
        viewAll ? "" : (practice?.provider_id as string),
        Boolean(
            !(
                statusFilter &&
                statusFilter.length === 1 &&
                /,+/.test(statusFilter[0])
            )
        )
    );

    const getAuthorTextToDisplay = getSelectedAuthorName(
        providers.data?.data,
        data?.data,
        authorFilter,
        authorNameFilter
    );

    const canCreateNote = useCanCreateNote();

    const doesNoteHaveBill = (note: INote) => {
        const notesTypesWithoutBill = [
            NoteTypes.SAFETY_PLAN,
            NoteTypes.SUPERVISION_NOTE,
            NoteTypes.GENERAL_UPDATE_NOTE,
            NoteTypes.SUPERVISOR_CHECK_IN,
            NoteTypes.PAUSE_NOTIFICATION_NOTE,
        ];
        return (
            !notesTypesWithoutBill.includes(note.note_type) &&
            Boolean(note.date_of_service)
        );
    };

    const userShouldNaviagteToBills = [
        UserRole.BILLER,
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
    ].some((role) => practice?.roles?.includes(role));

    const navigate = useNavigate();

    // Clicked note navigation for web
    const handleRowClick = (row: Row<INote>) => {
        if (
            loggedInUser.data &&
            practice?.provider_id &&
            row.original.status === NoteStatus.DRAFT
        ) {
            navigate(
                `/notes/${row.original.note_id}/${row.original.client.client_id}/edit`
            );
        } else if (
            row.original.status === NoteStatus.CODED &&
            doesNoteHaveBill(row.original) &&
            userShouldNaviagteToBills
        ) {
            navigate(
                `/billing-and-claims/${row.original.client.client_id}/${row.original.note_id}?viewFrom=session_note`
            );
        } else {
            navigate(
                `/notes/${row.original.client.client_id}/${row.original.note_id}?viewFrom=session_note`
            );
        }
    };

    // Clicked note navigation for mobile
    const handleMobileNoteClick = (note: INote) => {
        if (
            loggedInUser.data &&
            practice?.provider_id &&
            note.status === NoteStatus.DRAFT
        ) {
            navigate(`/notes/${note.note_id}/${note.client.client_id}/edit`);
        } else if (
            note.status === NoteStatus.CODED &&
            doesNoteHaveBill(note) &&
            userShouldNaviagteToBills
        ) {
            navigate(
                `/billing-and-claims/${note.client.client_id}/${note.note_id}?viewFrom=session_note`
            );
        } else {
            navigate(
                `/notes/${note.client.client_id}/${note.note_id}?viewFrom=session_note`
            );
        }
    };

    /** FILTER INPUTS ONCHANGE FUNCTIONS */
    const handleSetClientNameFilter = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetAuthorFilter = (author: string, authorName = "") => {
        const nextSearchParams = updateAuthorFilterSearchParams(
            searchParams,
            author,
            authorName
        );
        localStorage.setItem("notelist_filters", nextSearchParams.toString());
        setSearchParams(nextSearchParams);
    };

    const handleSetTypeFilter = (type: string) => {
        if (noteTypeFilter.includes(type)) {
            searchParams.delete("type[]", type);
        } else {
            searchParams.append("type[]", type);
        }
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetStatusFilter = (status: string) => {
        if (statusFilter.includes(status)) {
            searchParams.delete("status[]", status);
        } else {
            searchParams.append("status[]", status);
        }
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };
    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        setInsuranceProviderIdsFilter(searchParams, insuranceProviderIds);
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetCptCodeFilter = (cptCode: string) => {
        if (cptCode === "") searchParams.delete("cpt_code");
        else searchParams.set("cpt_code", cptCode);
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        if (archived === "") searchParams.delete("show_archived");
        else searchParams.set("show_archived", archived);
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFromDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date_created");
        else searchParams.set("from_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date_created");
        else searchParams.set("to_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        searchParams.set("page", "1");
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const getArchivedNoteFilterToDisplay = () => {
        if (showArchivedFilter === "true") {
            return "Archived notes";
        }
        return "Unarchived notes";
    };

    const handleSuccess = () => {
        setRowSelection({});
        setSelectedNotesId([]);
    };

    const confirmCodes = useChangeCptCode();
    const recalculateUnits = useRecalculateUnitMetadata();

    const selectedIds = getSelectedData
        ?.filter(Boolean)
        .map((item: Row<INote>) => item.original.note_id) as string[];

    const mobileListRef = React.useRef<HTMLDivElement>(null);

    // Array of details of the selected notes(web)
    const notesDetails =
        selectedIds &&
        selectedIds.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    // Array of details of the selected notes(mobile)
    const selectedNotesDetails =
        selectedNotesId &&
        selectedNotesId.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    const detailsArray =
        selectedNotesId.length > 0
            ? (selectedNotesDetails?.filter(Boolean) as INote[])
            : (notesDetails?.filter(Boolean) as INote[]);
    const hasExportableSelectedNote =
        detailsArray?.some(isNotePdfExportable) ?? false;

    // Details of a single note
    const noteDetails = data?.data.find((note) => {
        return note.note_id === codeToConfirmId;
    });

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const handleConfirmCodes = (noteId?: string) => {
        interface ConfirmCodePayload {
            [noteId: string]: {
                cpt_code: string;
                modifier_codes: string[];
                confirm_code: boolean;
            };
        }

        // Multi-selected notes
        const confirmCodesPayload: ConfirmCodePayload =
            detailsArray && detailsArray?.length !== 0
                ? detailsArray
                      .filter(Boolean)
                      .reduce((payload: ConfirmCodePayload, note: INote) => {
                          const noteIdForPayload = note.note_id;
                          return {
                              ...payload,
                              [noteIdForPayload]: {
                                  cpt_code: note.cpt_code || "",
                                  modifier_codes: note.modifier_codes || [],
                                  confirm_code: true,
                              },
                          };
                      }, {})
                : {};

        // Single note action
        const confirmCodePayload = {
            [String(noteId)]: {
                cpt_code: noteDetails?.cpt_code || "",
                modifier_codes: noteDetails?.modifier_codes || [],
                confirm_code: true,
            },
        };

        confirmCodes.mutate(
            detailsArray && detailsArray?.length !== 0
                ? confirmCodesPayload
                : confirmCodePayload,
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({
                        queryKey: [`notes`],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Cpt code confirmed successfully",
                    });
                    handleSuccess();
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not confirm code at this moment, please try again",
                    });
                },
            }
        );
    };

    const handleRecalculateUnits = () => {
        const noteIds =
            detailsArray && detailsArray.length !== 0
                ? detailsArray
                      .filter(Boolean)
                      .map((note: INote) => note.note_id)
                : [];

        recalculateUnits.mutate(
            { note_ids: noteIds, dry_run: false },
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({
                        queryKey: [`notes`],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "CPT Unit recalculated successfully",
                    });
                    handleSuccess();
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not recalculate CPT unit at this time",
                    });
                },
            }
        );
    };

    const exportNotes = useExportNotes();

    const canExportNotes = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const handleExportNotes = (noteId?: string) => {
        const notesToExport = noteId
            ? data?.data.filter((note) => note.note_id === noteId) ?? []
            : detailsArray ?? [];
        const sessionNoteIds = getPdfExportableNoteIds(notesToExport);

        if (sessionNoteIds.length === 0) {
            toast({
                mode: "warning",
                message: "The selected notes are unavailable for PDF export",
            });
            return;
        }

        const dataToSend = { session_note_ids: sessionNoteIds };

        exportNotes.mutate(dataToSend, {
            onSuccess: async (res) => {
                handleDialogInView("loader");
                await new Promise<void>((resolve) => {
                    const timer = setTimeout(() => {
                        clearTimeout(timer);
                        setJobId(res.data.export_job_id);
                        setPdfCount(res.data.expected_pdf_count);
                        resolve();
                    }, 8000);
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.message || "Could not export notes at this time",
                });
            },
        });
    };

    const deleteNote = useDeleteDraftNote();

    // Function to call for deleting note
    const handleDeleteNote = () => {
        const noteIds = selectedIds?.length > 0 ? selectedIds : selectedNotesId;

        deleteNote.mutate(
            noteIds.length === 0 && noteDetails?.note_id
                ? [noteDetails?.note_id]
                : (noteIds?.filter(Boolean) as string[]),
            {
                async onSuccess(res) {
                    await queryClient.invalidateQueries({
                        queryKey: [`notes`],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Draft note(s) deleted successfully",
                    });

                    // Close delete confirmation modal
                    handleSuccess();
                    handleDialogInView(null);
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Cannot delete note(s) at this moment, please try again",
                    });
                },
            }
        );
    };

    const unarchiveNote = useChangeNoteArchiveStatus();

    const handleUnarchive = (id?: string) => {
        const noteIds = selectedIds?.length > 0 ? selectedIds : selectedNotesId;

        const dataToSend = {
            note_ids_to_archive: noteIds.length === 0 && id ? [id] : noteIds,
            archive_status: false,
        };

        unarchiveNote.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Note unarchived successfully",
                });

                setRowSelection({});
                setSelectedNotesId([]);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not unarchive note at this time",
                });
            },
        });
    };

    const skipSupervisorReviewMutation = useSkipSupervisorReview();

    const handleSkipSupervisorReview = () => {
        const noteIds = detailsArray?.map((note: INote) => note.note_id) || [];

        skipSupervisorReviewMutation.mutate(noteIds, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Supervisor review skipped",
                });

                handleSuccess();
                handleDialogInView(null);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not skip supervisor review at this time",
                });
            },
        });
    };

    const exportNote = useExportPdfs(
        {
            export_job_id: jobId as string,
            pdf_type: "session_note",
            expected_pdf_count: pdfCount,
        },
        Boolean(jobId)
    );

    if (jobId && !exportNote.isLoading) {
        setJobId(null);
        handleDialogInView(null);
        setRowSelection({});
    }

    const showActions =
        practice?.roles?.includes(UserRole.CODER) ||
        practice?.roles?.includes(UserRole.SUPER_ADMIN);

    const canApproveNote =
        (viewAll &&
            loggedInUser.data?.user_id !== noteDetails?.provider?.user_id &&
            !noteDetails?.is_archived &&
            !practice?.roles?.includes(UserRole.AUDITOR_ADMIN)) ||
        (viewAll &&
            detailsArray?.every(
                (note: INote) =>
                    note?.provider?.user_id !== loggedInUser.data?.user_id
            ) &&
            detailsArray?.every((note: INote) => note?.is_archived === false) &&
            !practice?.roles?.includes(UserRole.AUDITOR_ADMIN));

    const canArchiveNote = [
        UserPermisions.CLIENT_NOTES_ALL_READ,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
    ].some((permission) => practice?.permissions?.includes(permission));

    const canArchiveCodedNote = [
        UserRole.SUPER_ADMIN,
        UserRole.CODER,
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ].some((role) => practice?.roles?.includes(role));

    const showArchiveOption = (row: Row<INote>) =>
        (canArchiveNote &&
            (row.original.status !== NoteStatus.CODED ||
                !row.original.date_of_service)) ||
        (canArchiveCodedNote && row.original.status === NoteStatus.CODED);

    const selectedNotesHaveClaim = detailsArray?.some((note) =>
        Boolean(note?.claim_id)
    );

    const isBillerOrBillerAdmin = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ]);

    const canArchiveNoteWithClaim = (note: INote) =>
        !note.claim_id || isBillerOrBillerAdmin;

    const canArchiveSelectedNotesWithClaim = () =>
        !selectedNotesHaveClaim || isBillerOrBillerAdmin;

    const showBulkArchiveBtn = () =>
        (canArchiveNote &&
            detailsArray?.every(
                (note: INote) =>
                    note?.status !== NoteStatus.CODED || !note?.date_of_service
            )) ||
        (canArchiveCodedNote &&
            detailsArray?.every(
                (note: INote) => note?.status === NoteStatus.CODED
            ));

    const canViewProvidersFilter =
        (practice?.permissions?.includes(UserPermisions.AUDIT_DASHBOARD_READ) &&
            viewAll) ||
        (!practice?.provider_id && viewAll);

    const showBulkFlagNotesBtn =
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN) &&
        detailsArray?.every(
            (note: INote) =>
                note?.flagged_for_audit_status !==
                FlaggedNoteForAuditStatus.FLAGGED
        ) &&
        detailsArray?.every(
            (note: INote) =>
                note?.status === NoteStatus.PENDING_CODE_REVIEW ||
                note?.status === NoteStatus.CODED
        );

    const showBulkUnflagNotesBtn =
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN) &&
        detailsArray?.every(
            (note: INote) =>
                note?.flagged_for_audit_status ===
                FlaggedNoteForAuditStatus.FLAGGED
        );

    const showBulkSkipSupervisorReviewBtn =
        practice?.permissions?.includes(
            UserPermisions.NOTE_SUPERVISOR_REVIEW_SKIP
        ) &&
        detailsArray?.every(
            (note: INote) =>
                note?.status === NoteStatus.PENDING_SUPERVISOR_REVIEW &&
                note?.is_archived === false
        );

    const showFlagOption = (row: Row<INote>) =>
        row.original.flagged_for_audit_status !==
            FlaggedNoteForAuditStatus.FLAGGED &&
        (row.original.status === NoteStatus.CODED ||
            row.original.status === NoteStatus.PENDING_CODE_REVIEW) &&
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN);

    // Toggles note selection for mobile
    const toggleNoteSelection = (noteId: string) => {
        if (selectedNotesId.includes(noteId)) {
            setSelectedNotesId(selectedNotesId.filter((id) => id !== noteId));
        } else {
            setSelectedNotesId([...selectedNotesId, noteId]);
        }
    };

    const handleNoteHasMoreActions = (row: Row<INote>) => {
        if (
            (showActions &&
                row.original.status === NoteStatus.PENDING_CODE_REVIEW) ||
            row.original.status === NoteStatus.DRAFT ||
            (canApproveNote &&
                row.original.status === NoteStatus.PENDING_SUPERVISOR_REVIEW) ||
            showArchiveOption(row)
        ) {
            return true;
        }
        return false;
    };

    // Handle approve action clicked
    const handleApproveActionClicked = (note: INote) => {
        if (note.note_type === NoteTypes.SAFETY_PLAN) {
            toast({
                mode: "warning",
                message:
                    "Before giving your approval, please thoroughly review this safety plan note by opening it and going through its contents.",
            });
        } else {
            handleDialogInView("approve");
        }
    };

    // Table columns
    const columns: ColumnDef<INote>[] = React.useMemo(
        () => [
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        handleChange={table.getToggleAllRowsSelectedHandler()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={table.getIsAllRowsSelected()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                ),
            },
            {
                accessorKey: "date_of_service",
                header: "DATE OF SERVICE",
                cell: ({ row }) =>
                    row.original.date_of_service
                        ? formatDate(row.original.date_of_service)
                        : "--",
            },
            {
                accessorKey: "note_type",
                header: "NOTE TYPE",
                cell: ({ row }) =>
                    getNoteTypeTextWithTag(row.original.note_type),
            },
            {
                accessorKey: "client_name",
                header: "CLIENT",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.client.first_name,
                            lastName: row.original.client?.last_name,
                        })}
                    </span>
                ),
            },
            {
                accessorKey: "cpt_code",
                header: "CPT CODE(S)",
                cell: ({ row }) =>
                    getCptCodeToDisplay({
                        noteType: row.original.note_type,
                        cptCode: row.original.cpt_code,
                        additionalCptCodes: row.original.additional_cpt_codes,
                        unitCalculation:
                            row.original?.note_metadata?.unit_calculation,
                    }),
            },
            {
                accessorKey: "external_reference_id",
                header: "claim ref no",
                cell: ({ row }) => row.original.external_reference_id ?? "--",
            },
            {
                accessorKey: "author",
                header: "CLINICIAN",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.author.first_name,
                            lastName: row.original.author.last_name,
                        })}
                    </span>
                ),
            },
            {
                accessorKey: "created_at",
                header: "CREATED ON",
                cell: ({ row }) => formatDate(row.original.created_at),
            },
            {
                accessorKey: "status",
                header: "NOTE STATUS / FLAGGED STATUS",
                cell: ({ row }) => (
                    <div className="flex flex-col items-start gap-1">
                        {getNoteStatusTag(row.original.status as NoteStatus)}
                        {row.original.flagged_for_audit_status
                            ? getFlaggedNoteStatusTag(
                                  row.original.flagged_for_audit_status
                              )
                            : null}
                    </div>
                ),
            },
            {
                accessorKey: "client",
                header: "INSURANCE PROVIDER",
                cell: ({ row }) => (
                    <span
                        className={
                            row.original.client.self_pay && !showInsOverSelfPay
                                ? "normal-case"
                                : "uppercase"
                        }
                    >
                        {row.original.client.self_pay && !showInsOverSelfPay
                            ? "Self pay"
                            : (row.original.client.insurance_provider_name &&
                                  removeEnumUnderscore(
                                      row.original.client
                                          .insurance_provider_name || ""
                                  )) ||
                              "--"}
                    </span>
                ),
            },
            {
                accessorKey: "note_id",
                header: "",
                cell: ({ row }) =>
                    handleNoteHasMoreActions(row) && (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <MoreButton
                                    onPointerDown={() => {
                                        setCodeToConfirmId(
                                            () => row.original.note_id
                                        );
                                    }}
                                />
                            </DropdownTrigger>
                            <DropdownContent align="end" width="auto">
                                {canExportNotes &&
                                    isNotePdfExportable(row.original) && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExportNotes(
                                                    row.original.note_id
                                                );
                                            }}
                                        >
                                            Export to pdf
                                        </DropdownItem>
                                    )}
                                {showArchiveOption(row) &&
                                    canArchiveNoteWithClaim(row.original) &&
                                    row.original.is_archived === false &&
                                    row.original.status !==
                                        NoteStatus.DRAFT && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDialogInView(
                                                    "archive-note"
                                                );
                                            }}
                                        >
                                            Archive note
                                        </DropdownItem>
                                    )}
                                {showArchiveOption(row) &&
                                    row.original.is_archived && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUnarchive(
                                                    row.original.note_id
                                                );
                                            }}
                                        >
                                            Unarchive note
                                        </DropdownItem>
                                    )}
                                {showFlagOption(row) && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogInView("flag_note");
                                        }}
                                    >
                                        Flag for audit
                                    </DropdownItem>
                                )}
                                {showActions &&
                                    row.original.status ===
                                        NoteStatus.PENDING_CODE_REVIEW &&
                                    row.original.flagged_for_audit_status !==
                                        FlaggedNoteForAuditStatus.FLAGGED &&
                                    row.original.note_type !==
                                        NoteTypes.CANCELLATION_NOTE && (
                                        <>
                                            <DropdownItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleConfirmCodes(
                                                        row.original.note_id
                                                    );
                                                }}
                                                disabled={
                                                    confirmCodes.isLoading
                                                }
                                            >
                                                Confirm Code
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDialogInView(
                                                        "change-code"
                                                    );
                                                }}
                                                disabled={
                                                    confirmCodes.isLoading
                                                }
                                            >
                                                Change Code
                                            </DropdownItem>
                                        </>
                                    )}
                                {row.original.status ===
                                    NoteStatus.PENDING_SUPERVISOR_REVIEW &&
                                    canApproveNote && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleApproveActionClicked(
                                                    row.original
                                                );
                                            }}
                                        >
                                            Approve and sign note
                                        </DropdownItem>
                                    )}
                                {row.original.status === NoteStatus.DRAFT && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogInView(
                                                "delete-confirmation"
                                            );
                                        }}
                                        className="text-danger"
                                    >
                                        Delete Note
                                    </DropdownItem>
                                )}
                                {row.original.flagged_for_audit_status ===
                                    FlaggedNoteForAuditStatus.FLAGGED &&
                                    !practice?.roles?.includes(
                                        UserRole.AUDITOR_ADMIN
                                    ) &&
                                    practice?.permissions?.includes(
                                        UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                                    ) && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDialogInView(
                                                    "unflag_note"
                                                );
                                            }}
                                        >
                                            Unflag note
                                        </DropdownItem>
                                    )}
                            </DropdownContent>
                        </Dropdown>
                    ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Check if  mutation is currently running, returns 0 || 1
    const changeNoteIsMutating = useIsMutating(["change-code"]);
    const archiveNoteIsMutating = useIsMutating(["archive-note"]);
    const approveNoteIsMutating = useIsMutating(["approve"]);
    const flagNoteIsMutating = useIsMutating(["flag-note"]);

    // Check if selected notes(mobile) contain notes with note type of safety_plan
    const notesToApproveHaveSafetyPlanMobile = data?.data?.filter(
        (note) =>
            selectedNotesId?.includes(note.note_id) &&
            note.note_type === NoteTypes.SAFETY_PLAN
    );

    // Check if selected notes(web) contain notes with note type of safety_plan
    const notesToApproveHaveSafetyPlanWeb = data?.data?.filter(
        (note) =>
            selectedIds?.includes(note.note_id) &&
            note.note_type === NoteTypes.SAFETY_PLAN
    );

    // Check to determine whether to show warning alert in the approve note modal
    const doesSelectedNotesHaveSafetyPlan =
        (notesToApproveHaveSafetyPlanMobile &&
            notesToApproveHaveSafetyPlanMobile.length > 0) ||
        (notesToApproveHaveSafetyPlanWeb &&
            notesToApproveHaveSafetyPlanWeb.length > 0);

    const handleApproveActionClick = () => {
        if (
            (notesToApproveHaveSafetyPlanWeb?.length === 0 &&
                notesToApproveHaveSafetyPlanWeb &&
                notesToApproveHaveSafetyPlanWeb?.length > 0) ||
            (notesToApproveHaveSafetyPlanMobile?.length === 0 &&
                notesToApproveHaveSafetyPlanMobile &&
                notesToApproveHaveSafetyPlanMobile?.length > 0)
        ) {
            toast({
                mode: "warning",
                message:
                    "You've only selected safety plan note(s), please thoroughly review each by opening and going through its contents before giving your approval.",
            });
        } else {
            handleDialogInView("approve");
        }
    };

    React.useEffect(() => {
        if (setNotesCount) {
            if (error) setNotesCount("");
            else if (isLoading) setNotesCount("");
            else if (data && data?.total_count > 0) {
                setNotesCount(String(data.total_count));
            } else if (data && data.data.length === 0) {
                setNotesCount("");
            }
        }
    }, [data, error, isLoading, setNotesCount]);

    const pageToRecover = data
        ? getNotesRecoveryPage({
              count: data.count,
              currentPage: pageFilter,
              totalCount: data.total_count,
              totalPages: data.total_pages,
          })
        : null;

    React.useEffect(() => {
        if (pageToRecover) {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set("page", String(pageToRecover));
            localStorage.setItem(
                "notelist_filters",
                nextSearchParams.toString()
            );
            setSearchParams(nextSearchParams, { replace: true });
        }
    }, [pageToRecover, searchParams, setSearchParams]);

    React.useEffect(() => {
        searchParams.set("sort_attr", sortConfig.key);
        searchParams.set("sort_order", sortConfig.direction);
        localStorage.setItem("notelist_filters", searchParams.toString());
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig]);

    React.useEffect(() => {
        if (
            statusFilter &&
            statusFilter.length === 1 &&
            /,+/.test(statusFilter[0])
        ) {
            searchParams.delete("status[]");
            statusFilter[0].split(",").forEach((status) => {
                searchParams.append("status[]", status);
            });

            setSearchParams(searchParams);
        }
    }, [searchParams, setSearchParams, statusFilter]);

    return (
        <>
            <Dialog open={dialogInView === "change-code"}>
                <DialogContent
                    title="Change code"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter
                    variant="center"
                    cancelText="Cancel"
                    saveText="Change code"
                    submitBtnFormValue="change-cpt-code"
                    isSubmitBtnDisabled={changeNoteIsMutating > 0}
                    isCancelBtnDisabled={changeNoteIsMutating > 0}
                >
                    {isPsychNote(noteDetails?.note_type) ? (
                        <ChangeCodeFormPsych
                            onFormSubmit={() => {
                                handleDialogInView(null);
                                setRowSelection({});
                                setSelectedNotesId([]);
                            }}
                            cptCode={noteDetails?.cpt_code || ""}
                            notes={detailsArray}
                            showDetails={false}
                            note={noteDetails}
                        />
                    ) : (
                        <ChangeCode
                            note={noteDetails}
                            onFormSubmit={() => {
                                handleDialogInView(null);
                                setRowSelection({});
                                setSelectedNotesId([]);
                            }}
                            showDetails={false}
                            notes={detailsArray}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "loader"}>
                <DialogContent
                    title="Export in progress"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter={false}
                >
                    <ExportLoaderAndText />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "archive-note"}>
                <DialogContent
                    title="Archive Note"
                    variant="center"
                    showFooter
                    saveText="Archive Note"
                    submitBtnFormValue="archive-note"
                    handleCloseDialog={() => handleDialogInView(null)}
                    isSubmitBtnDisabled={archiveNoteIsMutating > 0}
                    isCancelBtnDisabled={archiveNoteIsMutating > 0}
                >
                    <ArchiveNote
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            setRowSelection({});
                            setSelectedNotesId([]);
                        }}
                        noteId={noteDetails?.note_id as string}
                        selectedIds={selectedIds}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={
                    dialogInView === "flag_note" ||
                    dialogInView === "unflag_note"
                }
            >
                <DialogContent
                    title={
                        dialogInView === "flag_note"
                            ? "Flag for Audit"
                            : "Unflag Note"
                    }
                    variant="center"
                    showFooter
                    saveText={
                        dialogInView === "flag_note"
                            ? "Flag for Audit"
                            : "Unflag Note"
                    }
                    submitBtnFormValue="flag-note"
                    handleCloseDialog={() => {
                        handleDialogInView(null);
                        setRowSelection({});
                    }}
                    isSubmitBtnDisabled={flagNoteIsMutating > 0}
                    isCancelBtnDisabled={flagNoteIsMutating > 0}
                >
                    <FlagNoteForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            setRowSelection({});
                            setSelectedNotesId([]);
                        }}
                        noteId={noteDetails?.note_id}
                        selectedIds={selectedIds}
                        action={
                            dialogInView === "flag_note" ? "flag" : "unflag"
                        }
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "approve"}>
                <DialogContent
                    title="Approve and sign note"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    saveText="Approve and sign note"
                    showFooter
                    submitBtnFormValue="note-approval"
                    isSubmitBtnDisabled={approveNoteIsMutating > 0}
                    isCancelBtnDisabled={approveNoteIsMutating > 0}
                >
                    <>
                        {doesSelectedNotesHaveSafetyPlan && (
                            <div className="mb-[--spacing-header]">
                                <Alert
                                    type="warning"
                                    description="Please note that selected safety plan notes will be excluded from this action. Kindly thoroughly review each safety plan note by opening it and going through its contents before approving and signing them individually."
                                    style={{ width: "100%" }}
                                />
                            </div>
                        )}
                        <ApproveNote
                            noteId={noteDetails?.note_id as string}
                            selectedIds={selectedIds}
                            showDetails={false}
                            onFormSubmit={() => {
                                handleDialogInView(null);
                                setRowSelection({});
                                setSelectedNotesId([]);
                            }}
                        />
                    </>
                </DialogContent>
            </Dialog>
            <AlertDialog open={dialogInView === "delete-confirmation"}>
                <AlertDialogContent
                    title=" Delete draft note?"
                    description="Are you sure you want to delete this draft note?"
                    isDeleting
                    saveText="Delete Draft"
                    onCancelClick={() => handleDialogInView(null)}
                    onSaveClick={handleDeleteNote}
                    isSaveBtnDisabled={deleteNote.isLoading}
                    isCancelBtnDisabled={deleteNote.isLoading}
                />
            </AlertDialog>
            <Dialog open={dialogInView === "skip-supervisor-review"}>
                <DialogContent
                    title="Skip Supervisor Review"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter
                    saveText="Skip review"
                    variant="center"
                    onSaveClick={handleSkipSupervisorReview}
                    cancelText="Cancel"
                    isSubmitBtnDisabled={skipSupervisorReviewMutation.isLoading}
                    isCancelBtnDisabled={skipSupervisorReviewMutation.isLoading}
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            You are about to skip supervisor review and send
                            {detailsArray?.length === 1
                                ? " this note "
                                : " these notes "}
                            to code review
                        </h3>
                        <p className="text-gray font-normal text-base">
                            Do you wish to continue?
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
            <div>
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-[--spacing-items]">
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <SearchInput
                                defaultValue={clientFilter}
                                onChange={handleSetClientNameFilter}
                                containerClass="w-[400px] max-w-full"
                                placeholder="Search by client name or chart number"
                            />
                        </div>
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Note type</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent
                                        align="end"
                                        width="auto"
                                        maxHeight={216}
                                    >
                                        {Object.values(NoteTypes).map(
                                            (type) => (
                                                <DropdownItem
                                                    key={type}
                                                    onClick={() =>
                                                        handleSetTypeFilter(
                                                            type
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={type}
                                                    values={noteTypeFilter}
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            NoteLabels[type]
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Status</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent
                                        align="end"
                                        width="auto"
                                        maxHeight={216}
                                    >
                                        {Object.values(NoteStatus).map(
                                            (status) => (
                                                <DropdownItem
                                                    key={status}
                                                    onClick={() =>
                                                        handleSetStatusFilter(
                                                            status
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={status}
                                                    values={statusFilter}
                                                    hidden={
                                                        !practice?.provider_id &&
                                                        status ===
                                                            NoteStatus.DRAFT
                                                    }
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            status
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                {canViewProvidersFilter && (
                                    <InsuranceProviderFilter
                                        values={insuranceProviderIdsFilter}
                                        onChange={
                                            handleInsuranceProviderIdsChange
                                        }
                                    />
                                )}
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {cptCodeFilter || "CPT code"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent maxHeight={216}>
                                        <DropdownItem
                                            isSelected={!cptCodeFilter}
                                            onClick={() =>
                                                handleSetCptCodeFilter("")
                                            }
                                        >
                                            All
                                        </DropdownItem>
                                        {CPT_CODES.map((code) => (
                                            <DropdownItem
                                                key={code}
                                                isSelected={
                                                    cptCodeFilter === code
                                                }
                                                onClick={() =>
                                                    handleSetCptCodeFilter(code)
                                                }
                                            >
                                                {removeEnumUnderscore(code)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                {canViewProvidersFilter && (
                                    <Popover
                                        open={showAuthorPopover}
                                        onOpenChange={(open) => {
                                            setShowAuthorPopover(open);
                                            handleResetProvidersSearchString();
                                        }}
                                    >
                                        <PopoverTrigger
                                            asChild
                                            onClick={() =>
                                                setShowAuthorPopover(true)
                                            }
                                        >
                                            <FilterButton
                                                className={
                                                    authorFilter
                                                        ? "capitalize"
                                                        : "normal-case"
                                                }
                                            >
                                                {getAuthorTextToDisplay
                                                    ? truncateString(
                                                          getAuthorTextToDisplay
                                                      )
                                                    : "Clinician"}
                                            </FilterButton>
                                        </PopoverTrigger>
                                        <PopoverContent align="start">
                                            <SearchInput
                                                placeholder="Search by name"
                                                onChange={(e) =>
                                                    setAuthorSearchString(
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                                {providers.isLoading ? (
                                                    <Loader wrapperClassName="h-[150px]" />
                                                ) : null}
                                                {providers.error && (
                                                    <ListState
                                                        isError
                                                        context="general"
                                                        stateHelperText="Please try again later"
                                                        errorMsg={
                                                            providers.error
                                                                ?.response?.data
                                                                ?.message ||
                                                            "Error loading authors"
                                                        }
                                                        cardClass="h-[150px]"
                                                    />
                                                )}
                                                {providers.data?.data &&
                                                    providers.isSuccess && (
                                                        <>
                                                            <Button
                                                                size="auto"
                                                                variant="can-hover"
                                                                className="font-medium capitalize w-full justify-start shrink-0 mt-2"
                                                                onClick={() => {
                                                                    handleSetAuthorFilter(
                                                                        ""
                                                                    );
                                                                    setShowAuthorPopover(
                                                                        false
                                                                    );
                                                                    handleResetProvidersSearchString();
                                                                }}
                                                            >
                                                                All
                                                            </Button>
                                                            {providers?.data?.data.map(
                                                                (provider) => (
                                                                    <Button
                                                                        key={
                                                                            provider.provider_id
                                                                        }
                                                                        size="auto"
                                                                        variant="can-hover"
                                                                        className="font-medium capitalize w-full justify-start shrink-0"
                                                                        onClick={() => {
                                                                            handleSetAuthorFilter(
                                                                                provider.user_id,
                                                                                provider.provider_name
                                                                            );
                                                                            setShowAuthorPopover(
                                                                                false
                                                                            );
                                                                            handleResetProvidersSearchString();
                                                                        }}
                                                                    >
                                                                        {displayNameInRightFormat(
                                                                            provider.provider_name
                                                                        )}
                                                                        {authorFilter ===
                                                                        provider.user_id ? (
                                                                            <CheckPrimaryColorIcon />
                                                                        ) : null}
                                                                    </Button>
                                                                )
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {getArchivedNoteFilterToDisplay()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width={180}>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "true"
                                                )
                                            }
                                        >
                                            Archived notes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "false"
                                                )
                                            }
                                        >
                                            Unarchived notes
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Created
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="end"
                                        className="flex gap-y-2"
                                    >
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={
                                                    fromDateCreatedFilterToDate
                                                }
                                                onChange={
                                                    handleFromDateCreatedFilterChange
                                                }
                                                maxDate={
                                                    toDateCreatedFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateCreatedFilterToDate}
                                            onChange={
                                                handleToDateCreatedFilterChange
                                            }
                                            minDate={
                                                fromDateCreatedFilterToDate
                                            }
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Of Service
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={fromDateFilterToDate}
                                                onChange={
                                                    handleFromDateFilterChange
                                                }
                                                maxDate={
                                                    toDateFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            minDate={fromDateFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="w-full min-[500px]:w-auto">
                                {canCreateNote ? (
                                    <Button
                                        className="w-full cursor-pointer"
                                        onClick={() => navigate("/add-note")}
                                    >
                                        Create note
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "status[]",
                                values: statusFilter,
                            },
                            {
                                key: "type[]",
                                values: noteTypeFilter.map(
                                    (type) => NoteLabels[type]
                                ),
                            },
                            {
                                key: "from_date_created",
                                values: fromDateCreatedFilter || "",
                            },
                            {
                                key: "to_date_created",
                                values: toDateCreatedFilter || "",
                            },
                            {
                                key: "from_date",
                                values: fromDateFilter || "",
                            },
                            {
                                key: "to_date",
                                values: toDateFilter || "",
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey="notelist_filters"
                    />
                </Card>

                {isLoading || pageToRecover ? <Loader /> : null}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display notes list at this time please try again later`
                        }
                    />
                )}
                {data &&
                    isSuccess &&
                    data.data.length === 0 &&
                    data.total_count === 0 && (
                        <ListState
                            stateHelperText="Notes list will appear here once they are added"
                            isError={false}
                            emptyMessage="No note added yet"
                        />
                    )}
                {isSuccess && data && data.data.length > 0 && (
                    <>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <div className="font-medium text-base shrink-0">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                    :
                                </div>

                                {canExportNotes && hasExportableSelectedNote && (
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleExportNotes()}
                                    >
                                        Export to pdf
                                    </Button>
                                )}

                                {showActions &&
                                    detailsArray?.every(
                                        (note: INote) =>
                                            note?.status ===
                                                NoteStatus.PENDING_CODE_REVIEW &&
                                            note?.note_type !==
                                                NoteTypes.CANCELLATION_NOTE &&
                                            !isPsychNote(note.note_type)
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleDialogInView(
                                                    "change-code"
                                                )
                                            }
                                            disabled={confirmCodes.isLoading}
                                        >
                                            Change code
                                        </Button>
                                    )}

                                {showActions &&
                                    detailsArray?.every(
                                        (note: INote) =>
                                            note?.status ===
                                                NoteStatus.PENDING_CODE_REVIEW &&
                                            note?.note_type !==
                                                NoteTypes.CANCELLATION_NOTE
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleConfirmCodes()}
                                            disabled={confirmCodes.isLoading}
                                        >
                                            Confirm code
                                        </Button>
                                    )}

                                {showActions &&
                                    detailsArray?.every(
                                        (note: INote) =>
                                            note?.status ===
                                                NoteStatus.PENDING_CODE_REVIEW &&
                                            isPsychNote(note?.note_type)
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={handleRecalculateUnits}
                                            disabled={
                                                recalculateUnits.isLoading
                                            }
                                        >
                                            Recalculate CPT codes
                                        </Button>
                                    )}
                                {detailsArray?.every(
                                    (note: INote) =>
                                        note?.status ===
                                        NoteStatus.PENDING_SUPERVISOR_REVIEW
                                ) &&
                                    canApproveNote &&
                                    practice?.provider_id && (
                                        <Button
                                            variant="secondary"
                                            onClick={handleApproveActionClick}
                                        >
                                            Approve and sign
                                        </Button>
                                    )}

                                {showBulkSkipSupervisorReviewBtn && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView(
                                                "skip-supervisor-review"
                                            )
                                        }
                                        disabled={
                                            skipSupervisorReviewMutation.isLoading
                                        }
                                    >
                                        Skip supervisor review
                                    </Button>
                                )}

                                {showBulkArchiveBtn() &&
                                    detailsArray?.every(
                                        (note: INote) =>
                                            note?.is_archived === false
                                    ) &&
                                    canArchiveSelectedNotesWithClaim() &&
                                    detailsArray?.every(
                                        (note: INote) =>
                                            note?.status !== NoteStatus.DRAFT
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleDialogInView(
                                                    "archive-note"
                                                )
                                            }
                                        >
                                            Archive note
                                        </Button>
                                    )}

                                {showBulkArchiveBtn() &&
                                    detailsArray?.every(
                                        (note: INote) => note?.is_archived
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleUnarchive()}
                                        >
                                            Unarchive note
                                        </Button>
                                    )}

                                {showBulkFlagNotesBtn && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView("flag_note")
                                        }
                                    >
                                        Flag for audit
                                    </Button>
                                )}
                                {showBulkUnflagNotesBtn && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView("unflag_note")
                                        }
                                    >
                                        Unflag note
                                    </Button>
                                )}

                                {detailsArray?.every(
                                    (note: INote) =>
                                        note?.status === NoteStatus.DRAFT
                                ) && (
                                    <Button
                                        onClick={() =>
                                            handleDialogInView(
                                                "delete-confirmation"
                                            )
                                        }
                                        variant="danger"
                                    >
                                        Delete note
                                    </Button>
                                )}
                            </div>
                        )}
                        {selectedNotesId.length > 0 && (
                            <div className="mb-16 flex items-center gap-x-8 text-sm">
                                <div>
                                    {`${selectedNotesId.length}  selected`} :
                                </div>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton> Actions</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="auto">
                                        <div
                                            className={
                                                styles.action_dropdown_items
                                            }
                                        >
                                            {canExportNotes &&
                                                hasExportableSelectedNote && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleExportNotes()
                                                        }
                                                    >
                                                        Export to pdf
                                                    </DropdownItem>
                                                )}

                                            {detailsArray?.every(
                                                (note: INote) =>
                                                    note?.status ===
                                                    NoteStatus.DRAFT
                                            ) && (
                                                <DropdownItem
                                                    onClick={() =>
                                                        handleDialogInView(
                                                            "delete-confirmation"
                                                        )
                                                    }
                                                >
                                                    Delete note
                                                </DropdownItem>
                                            )}
                                            {showActions &&
                                                detailsArray.every(
                                                    (note: INote) =>
                                                        note.status ===
                                                        NoteStatus.PENDING_CODE_REVIEW
                                                ) && (
                                                    <>
                                                        <DropdownItem
                                                            onClick={() => {
                                                                handleConfirmCodes();
                                                            }}
                                                            disabled={
                                                                confirmCodes.isLoading
                                                            }
                                                        >
                                                            Confirm Code
                                                        </DropdownItem>
                                                        {detailsArray.every(
                                                            (note: INote) =>
                                                                note?.note_type !==
                                                                NoteTypes.CANCELLATION_NOTE
                                                        ) && (
                                                            <DropdownItem
                                                                onClick={() => {
                                                                    handleDialogInView(
                                                                        "change-code"
                                                                    );
                                                                }}
                                                                disabled={
                                                                    confirmCodes.isLoading
                                                                }
                                                            >
                                                                Change Code
                                                            </DropdownItem>
                                                        )}
                                                        {detailsArray.every(
                                                            (note: INote) =>
                                                                isPsychNote(
                                                                    note?.note_type
                                                                )
                                                        ) && (
                                                            <DropdownItem
                                                                onClick={
                                                                    handleRecalculateUnits
                                                                }
                                                                disabled={
                                                                    recalculateUnits.isLoading
                                                                }
                                                            >
                                                                Recalculate CPT
                                                                codes
                                                            </DropdownItem>
                                                        )}
                                                    </>
                                                )}
                                            {detailsArray?.every(
                                                (note: INote) =>
                                                    note?.status ===
                                                    NoteStatus.PENDING_SUPERVISOR_REVIEW
                                            ) &&
                                                canApproveNote &&
                                                practice?.provider_id && (
                                                    <DropdownItem
                                                        onClick={
                                                            handleApproveActionClick
                                                        }
                                                    >
                                                        Approve and sign
                                                    </DropdownItem>
                                                )}
                                            {showBulkSkipSupervisorReviewBtn && (
                                                <DropdownItem
                                                    onClick={() =>
                                                        handleDialogInView(
                                                            "skip-supervisor-review"
                                                        )
                                                    }
                                                    disabled={
                                                        skipSupervisorReviewMutation.isLoading
                                                    }
                                                >
                                                    Skip supervisor review
                                                </DropdownItem>
                                            )}
                                            {showBulkArchiveBtn() &&
                                                detailsArray?.every(
                                                    (note: INote) =>
                                                        note?.is_archived ===
                                                        false
                                                ) &&
                                                canArchiveSelectedNotesWithClaim() &&
                                                detailsArray?.every(
                                                    (note: INote) =>
                                                        note?.status !==
                                                        NoteStatus.DRAFT
                                                ) && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleDialogInView(
                                                                "archive-note"
                                                            )
                                                        }
                                                    >
                                                        Archive note
                                                    </DropdownItem>
                                                )}
                                            {showBulkFlagNotesBtn && (
                                                <DropdownItem
                                                    onClick={() =>
                                                        handleDialogInView(
                                                            "flag_note"
                                                        )
                                                    }
                                                >
                                                    Flag for audit
                                                </DropdownItem>
                                            )}
                                            {practice?.permissions?.includes(
                                                UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                                            ) &&
                                                detailsArray?.every(
                                                    (note: INote) =>
                                                        note?.flagged_for_audit_status ===
                                                        FlaggedNoteForAuditStatus.FLAGGED
                                                ) && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleDialogInView(
                                                                "unflag_note"
                                                            )
                                                        }
                                                    >
                                                        Unflag note
                                                    </DropdownItem>
                                                )}
                                            {showBulkArchiveBtn() &&
                                                detailsArray?.every(
                                                    (note: INote) =>
                                                        note?.is_archived
                                                ) && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleUnarchive()
                                                        }
                                                    >
                                                        Unarchive note
                                                    </DropdownItem>
                                                )}
                                        </div>
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        )}
                        <div>
                            <Table
                                columns={columns}
                                data={data.data}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                handleRowClick={handleRowClick}
                                isColumnVisible={{
                                    provider_name: !(
                                        practice?.provider_id && !viewAll
                                    ),
                                    status: !(tabInView === "flagged_notes"),
                                }}
                                setGetSelectedData={setGetSelectedData}
                                className={styles.table}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                hasPagination={data.total_count > 20}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                }
                            />
                        </div>
                        <div
                            role="table"
                            ref={mobileListRef}
                            className="hidden"
                        >
                            <div className="flex flex-col gap-y-12">
                                {data.data.map((note) => (
                                    <MobileListItem
                                        key={note.note_id}
                                        onClickCheckButton={() =>
                                            toggleNoteSelection(note.note_id)
                                        }
                                        isSelected={selectedNotesId.includes(
                                            note.note_id
                                        )}
                                        showCheckButton
                                        topChildren={
                                            <div className="flex items-center gap-x-8 text-base font-semibold capitalize">
                                                {displayNameInRightFormat({
                                                    firstName:
                                                        note.client.first_name,
                                                    lastName:
                                                        note.client.last_name,
                                                })}
                                                {tabInView !==
                                                    "flagged_notes" && (
                                                    <span>
                                                        {getNoteStatusTag(
                                                            note.status
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        }
                                        bottomChildren={
                                            <Button
                                                variant="normal"
                                                size="auto"
                                                className="flex flex-col gap-y-8 w-full items-start"
                                                onClick={() =>
                                                    handleMobileNoteClick(note)
                                                }
                                            >
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="basis-1/2 text-gray font-semibold text-xs">
                                                        {formatDate(
                                                            note.date_of_service
                                                        )}
                                                    </span>
                                                    <span className="basis-1/2 font-semibold text-xs">
                                                        {
                                                            NoteLabels[
                                                                note.note_type
                                                            ]
                                                        }
                                                    </span>
                                                </div>
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Provider:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {note.provider
                                                            ? displayNameInRightFormat(
                                                                  {
                                                                      firstName:
                                                                          note
                                                                              .provider
                                                                              ?.first_name,
                                                                      lastName:
                                                                          note
                                                                              .provider
                                                                              ?.last_name,
                                                                  }
                                                              )
                                                            : "--"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Created on:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {formatDate(
                                                            note.created_at
                                                        )}
                                                    </span>
                                                </div>
                                                {!practice?.provider_id && (
                                                    <div className="flex items-center w-full text-left gap-x-4">
                                                        <span className="w-1/2 text-gray font-semibold text-xs">
                                                            Insurance Provider:
                                                        </span>
                                                        <span
                                                            className={
                                                                note.client
                                                                    .self_pay
                                                                    ? "normal-case w-1/2 font-semibold text-xs"
                                                                    : "uppercase w-1/2 font-semibold text-xs"
                                                            }
                                                        >
                                                            {note.client
                                                                .self_pay &&
                                                            !showInsOverSelfPay
                                                                ? "Self pay"
                                                                : (note.client
                                                                      .insurance_provider_name &&
                                                                      removeEnumUnderscore(
                                                                          note
                                                                              .client
                                                                              .insurance_provider_name ||
                                                                              ""
                                                                      )) ||
                                                                  "--"}
                                                        </span>
                                                    </div>
                                                )}
                                            </Button>
                                        }
                                    />
                                ))}
                            </div>
                            {data.total_count > 20 && (
                                <div className="bg-white p-12 rounded-r8 mt-16">
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
